import { apiFactory } from '../../ApiFactory';
import { type DisplayUserExtended, Request, type ResourceId } from '../../common';
import {
    type ApiResult,
    type BaseParams,
    type CreateYatPageParams,
    type EmojiId,
    type HttpStatus,
    type PostParams,
    type YatPageProfileResponse,
    type YatPageResponse,
    type YatPageVersionResponse,
} from '../../common/structs';
import { links } from '../../links';

async function createYatPage(
    params: BaseParams & PostParams<CreateYatPageParams>,
): Promise<ApiResult<YatPageResponse>> {
    return Request.postRequest<YatPageResponse>({
        ...params,
        target: links.yatPage().protected().upsert(),
        settings: {
            ...params.settings,
        },
    });
}

async function getYatPageDetails(params: BaseParams & { eid: string }): Promise<ApiResult<YatPageVersionResponse>> {
    return Request.getRequest<YatPageVersionResponse>({
        ...params,
        target: links.yatPage().public(params.eid).page(),
    });
}

async function updateYatPage(
    params: BaseParams & PostParams<CreateYatPageParams>,
): Promise<ApiResult<YatPageResponse>> {
    return Request.patchRequest<YatPageResponse>({
        ...params,
        target: links.yatPage().protected().upsert(),
        settings: {
            ...params.settings,
        },
    });
}

async function convertYatPage(params: BaseParams & PostParams & { eid: EmojiId }): Promise<ApiResult<HttpStatus>> {
    return Request.postRequest<HttpStatus>({
        ...params,
        target: links.yatPage().protected().convert(params.eid),
    });
}

const getYatPageProfile = (params: BaseParams): Promise<ApiResult<YatPageProfileResponse>> =>
    Request.getRequest({ ...params, target: links.yatPage().protected().upsert() });

const callGetYatPageProfile = (params: BaseParams): Promise<ApiResult<YatPageProfileResponse>> => {
    return apiFactory()
        .call(getYatPageProfile, {
            ...params,
        })
        .then((result) => {
            return result;
        });
};

async function discardYatPageEdits(
    params: BaseParams & PostParams & { pageId: string },
): Promise<ApiResult<YatPageResponse>> {
    return Request.postRequest<YatPageResponse>({
        ...params,
        target: links.yatPage().protected().discard(params.pageId),
    });
}

async function userById(params: BaseParams & { id: ResourceId }): Promise<ApiResult<DisplayUserExtended>> {
    return Request.getRequest<DisplayUserExtended>({
        ...params,
        target: links.user().public(params.id),
        settings: {
            ...params.settings,
        },
    });
}
export const callGetUserById = (params: BaseParams & { id: ResourceId }) => {
    return apiFactory()
        .call(userById, {
            ...params,
        })
        .then((result) => {
            if (result.ok) {
                return result.value;
            }
            const errorMessage = result?.error?.detail ?? `Error getting user data`;
            throw new Error(errorMessage);
        });
};

export const yatPage = {
    createYatPage,
    getYatPageDetails,
    updateYatPage,
    convertYatPage,
    discardYatPageEdits,
    callGetYatPageProfile,
};
